import React, { useEffect, useState, useMemo, useCallback, useReducer, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS, QUIZ_HINT_COST_POINTS, QUIZ_RUBRIC_MAX_POINTS, QUIZ_GAME_MAX_DELTA } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { rubricPointsToScoreDelta, formatRubricAsGamePointsLabel } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import "../StartGame.css";
import "./Scenario2.css";
import "../Scenario3/Scenario3.css";

const SC2_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
const SC2_HINT_THEME_CLASS = `${SC2_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();
const SC2_HINT_MARK_HTML = hintMarkPistaHtml();

const QUESTION_STEM =
	"Clasifica correctamente cada alerta para construir un primer mapa de diagnóstico";

const RETROALIMENTACION =
	"La integración de IA afecta a varias dimensiones de la empresa. Antes de elegir herramientas, el comité debe entender objetivos, datos, personas, riesgos y tecnología disponible.";

const HINT_HTML = `<div class="txtTexto1 mb-2">Pensad que integrar IA no es solo una decisión tecnológica. Revisad si cada alerta habla de objetivos de negocio, calidad de datos, personas, riesgos o herramientas.</div>`;

const CATEGORIES = [
	{ id: "negocio", title: "1. Negocio" },
	{ id: "datos", title: "2. Datos" },
	{ id: "personas", title: "3. Personas" },
	{ id: "riesgos", title: "4. Riesgos" },
	{ id: "tecnologia", title: "5. Tecnología" },
];

const ITEMS = [
	{
		id: "objetivo",
		text: "No existe un objetivo claro sobre qué problema debe resolver la IA.",
		category: "negocio",
	},
	{
		id: "sistemas",
		text: "Los datos están repartidos entre varios sistemas y algunos están duplicados.",
		category: "datos",
	},
	{
		id: "temor",
		text: "Parte del equipo teme que la IA sustituya puestos de trabajo.",
		category: "personas",
	},
	{
		id: "legal",
		text: "Legal advierte de posibles problemas con el uso de datos personales.",
		category: "riesgos",
	},
	{
		id: "integracion",
		text: "Las herramientas actuales no se integran bien entre departamentos.",
		category: "tecnologia",
	},
];

const CATEGORY_IDS = CATEGORIES.map((c) => c.id);
const DROP_ZONES = ["pool", ...CATEGORY_IDS];

const ACTIVITY_TEXT = Object.fromEntries(ITEMS.map((i) => [i.id, i.text]));

function initialPoolIds() {
	const shuffled = shuffleQuizOptions(ITEMS.map((r) => ({ id: r.id, text: r.text })));
	return shuffled.map((o) => o.id);
}

function emptyCategorySlots() {
	return Object.fromEntries(CATEGORY_IDS.map((id) => [id, []]));
}

function initialBoard() {
	return {
		pool: initialPoolIds(),
		...emptyCategorySlots(),
	};
}

function countCorrectPlacements(state) {
	let count = 0;
	for (const item of ITEMS) {
		const bucket = state[item.category];
		if (Array.isArray(bucket) && bucket.includes(item.id)) count++;
	}
	return count;
}

function rubricFromCorrectCount(correctCount) {
	if (correctCount >= 4) return 100;
	if (correctCount === 3) return 50;
	return 0;
}

function boardReducer(state, action) {
	switch (action.type) {
		case "RESET":
			return initialBoard();
		case "MOVE": {
			const { activityId, from, to } = action;
			if (from === to) return state;
			if (!DROP_ZONES.includes(from) || !DROP_ZONES.includes(to)) return state;
			const fromArr = state[from];
			if (!fromArr.includes(activityId)) return state;
			return {
				...state,
				[from]: fromArr.filter((id) => id !== activityId),
				[to]: [...state[to], activityId],
			};
		}
		default:
			return state;
	}
}

function Scenario2Quiz2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.SCENARIO2_PRIORITY_ORDER_QUIZ;
	const SCENARIO_ID = SCENARIOS.SCENARIO_2;

	const [board, dispatchBoard] = useReducer(boardReducer, undefined, initialBoard);
	const { pool } = board;

	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	const dndLocked = isCompleted || isSubmitting;

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const totalPlaced = useMemo(
		() => CATEGORY_IDS.reduce((sum, id) => sum + (board[id]?.length ?? 0), 0),
		[board]
	);

	const allPlaced = useMemo(
		() => pool.length === 0 && totalPlaced === ITEMS.length,
		[pool.length, totalPlaced]
	);

	const dragSessionRef = useRef(null);
	const ghostElRef = useRef(null);
	const [dragGhost, setDragGhost] = useState(null);

	const positionGhostFromSession = useCallback(() => {
		const el = ghostElRef.current;
		const d = dragSessionRef.current;
		if (!el || !d?.ghostShown) return;
		const maxW = typeof window !== "undefined" ? Math.min(d.width, window.innerWidth * 0.92) : d.width;
		el.style.width = `${maxW}px`;
		el.style.minHeight = `${Math.max(d.height, 28)}px`;
		el.style.left = `${d.lastClientX - d.offX}px`;
		el.style.top = `${d.lastClientY - d.offY}px`;
	}, []);

	const endPointerDrag = useCallback((el, pointerId) => {
		try {
			if (el?.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId);
		} catch {
			/* ignore */
		}
		dragSessionRef.current = null;
		setDragGhost(null);
		document.body.classList.remove("sc3-chrono-dnd-active");
	}, []);

	const applyDropFromPayload = useCallback((payload, target) => {
		if (!target?.kind) return;
		const to = target.kind;
		const from = payload.source;
		const activityId = payload.activityId;
		if (from === to) return;
		dispatchBoard({ type: "MOVE", activityId, from, to });
	}, []);

	const resolveDropTarget = useCallback((clientX, clientY) => {
		const stack = document.elementsFromPoint(clientX, clientY);
		for (const node of stack) {
			if (!(node instanceof Element)) continue;
			if (node.closest(".sc3-chrono-drag-ghost")) continue;
			const zone = node.closest("[data-sc2q2-drop]");
			if (zone) {
				const v = zone.getAttribute("data-sc2q2-drop");
				if (DROP_ZONES.includes(v)) return { kind: v };
			}
		}
		return null;
	}, []);

	const handleChipPointerDown = useCallback(
		(e, payload) => {
			if (dndLocked) return;
			if (e.pointerType === "mouse" && e.button !== 0) return;
			const rect = e.currentTarget.getBoundingClientRect();
			dragSessionRef.current = {
				payload,
				startX: e.clientX,
				startY: e.clientY,
				pointerId: e.pointerId,
				moved: false,
				ghostShown: false,
				offX: e.clientX - rect.left,
				offY: e.clientY - rect.top,
				width: rect.width,
				height: rect.height,
				lastClientX: e.clientX,
				lastClientY: e.clientY,
			};
			e.currentTarget.setPointerCapture(e.pointerId);
			e.preventDefault();
		},
		[dndLocked]
	);

	const handleChipPointerMove = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			d.lastClientX = e.clientX;
			d.lastClientY = e.clientY;
			const dx = e.clientX - d.startX;
			const dy = e.clientY - d.startY;
			if (dx * dx + dy * dy > 36) {
				d.moved = true;
				document.body.classList.add("sc3-chrono-dnd-active");
				if (!d.ghostShown) {
					d.ghostShown = true;
					const aid = d.payload.activityId;
					setDragGhost({
						activityId: aid,
						label: ACTIVITY_TEXT[aid] ?? aid,
						width: d.width,
						height: d.height,
					});
				}
			}
			if (d.ghostShown && ghostElRef.current) positionGhostFromSession();
		},
		[positionGhostFromSession]
	);

	useLayoutEffect(() => {
		if (!dragGhost) return;
		positionGhostFromSession();
	}, [dragGhost, positionGhostFromSession]);

	const handleChipPointerUpOrCancel = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			const el = e.currentTarget;
			const { payload, moved } = d;

			if (moved) {
				const target = resolveDropTarget(e.clientX, e.clientY);
				if (target) applyDropFromPayload(payload, target);
			}

			endPointerDrag(el, e.pointerId);
		},
		[applyDropFromPayload, endPointerDrag, resolveDropTarget]
	);

	useEffect(() => {
		return () => {
			document.body.classList.remove("sc3-chrono-dnd-active");
		};
	}, []);

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO2_CONTEXT3,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO2_CONTEXT3);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO2_CONTEXT3);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC2_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC2_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC2_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC2_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(QUIZ_HINT_COST_POINTS);
		}
	};

	function finishQuestion(rubricPointsAwarded, correctCount) {
		const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
		const scoreFromRubric = rubricPointsToScoreDelta(
			rubricPointsAwarded,
			QUIZ_RUBRIC_MAX_POINTS,
			QUIZ_GAME_MAX_DELTA
		);
		const attemptDelta = scoreFromRubric - hintThisAttempt;
		const newScore = pendingScore + attemptDelta;
		if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

		const capitalInicio = capitalAtQuizStartRef.current ?? 0;
		const netoEnEstaPregunta = newScore - capitalInicio;
		const rubricPass = rubricPointsAwarded >= QUIZ_RUBRIC_PASS_POINTS;
		const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
		const outcomeStyle = rubricPass ? "correct" : "incorrect";

		modalQuestionResolution({
			correct: rubricPass,
			question: "Mapa de diagnóstico",
			textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "orange")}
              <div class="txt28 bold pt-3">${formatRubricAsGamePointsLabel(rubricPointsAwarded)}</div>
              <div class="txt22 mt-2">${correctCount} de ${ITEMS.length} alertas clasificadas correctamente.</div>
              <div class="txt22 mt-1">${rubricPass ? "Has acertado." : "No has acertado."}</div>
              <div class="txt20 mt-2">Puntos en el marcador (esta pregunta): <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt${
					hintThisAttempt ? ` (incluye −${hintThisAttempt} pt por pista)` : ""
				}</div>
              ${retroalimentacionBlockHtml}`,
			customConfirmButtonHtml: "Continuar",
			color: "orange",
			themePopupClass: SC2_SWAL_POPUP_CLASS,
		}).then(() => {
			submitAnswer(newScore, rubricPass, { rubricPoints: rubricPointsAwarded });
		});
	}

	function renderDraggableChip(activityId, source) {
		return (
			<div
				key={`${source}-${activityId}`}
				tabIndex={0}
				className={`sc3-chrono-chip sc2-quiz2-classify-chip ${dragGhost?.activityId === activityId ? "sc3-chrono-chip--drag-source" : ""}`}
				onPointerDown={(e) => handleChipPointerDown(e, { source, activityId })}
				onPointerMove={handleChipPointerMove}
				onPointerUp={handleChipPointerUpOrCancel}
				onPointerCancel={handleChipPointerUpOrCancel}
				onLostPointerCapture={(e) => {
					if (dragSessionRef.current?.pointerId === e.pointerId) {
						endPointerDrag(e.currentTarget, e.pointerId);
					}
				}}
				onContextMenu={(ev) => ev.preventDefault()}
				title={ACTIVITY_TEXT[activityId]}
			>
				{ACTIVITY_TEXT[activityId]}
			</div>
		);
	}

	function checkAnswer() {
		if (!allPlaced) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Clasifica las cinco alertas en las categorías correspondientes (no puede quedar ninguna sin colocar).</div>`,
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC2_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const correctCount = countCorrectPlacements(board);
			const rubricPointsAwarded = rubricFromCorrectCount(correctCount);
			finishQuestion(rubricPointsAwarded, correctCount);
		});
	}

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz sc2-quiz2-classify">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 1 · Diagnóstico
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc3-chrono-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Arrastra cada alerta a la categoría que corresponda. Un solo intento: 4-5 aciertos, +10
								pt; 3 aciertos, +5 pt; 0-2 aciertos, 0 pt. La pista resta{" "}
								{QUIZ_HINT_COST_POINTS} pt al marcador.
							</span>
						</p>
					</div>

					<div className="sc2-quiz2-buckets" aria-label="Categorías de diagnóstico">
						{CATEGORIES.map((category) => {
							const chips = board[category.id] ?? [];
							return (
								<div
									key={category.id}
									className="sc2-quiz2-bucket"
									data-sc2q2-drop={category.id}
									aria-label={category.title}
								>
									<h2 className="sc2-quiz2-bucket-title">{category.title}</h2>
									<div className="sc2-quiz2-bucket-chips">
										{chips.length === 0 ? (
											<span className="sc2-quiz2-bucket-empty">Soltar aquí</span>
										) : (
											chips.map((id) => renderDraggableChip(id, category.id))
										)}
									</div>
								</div>
							);
						})}
					</div>

					<div className="sc3-chrono-pool-wrap sc2-quiz2-pool-wrap">
						<p className="sc3-chrono-pool-label mb-2">Alertas (arrastra desde aquí)</p>
						<div
							className="sc3-chrono-pool sc2-quiz2-pool"
							data-sc2q2-drop="pool"
							aria-label="Alertas sin clasificar"
						>
							{pool.length === 0 ? (
								<span className="sc3-chrono-pool-empty">Todas las fichas están clasificadas</span>
							) : (
								pool.map((activityId) => renderDraggableChip(activityId, "pool"))
							)}
						</div>
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />

			{typeof document !== "undefined" &&
				dragGhost &&
				createPortal(
					<div
						ref={ghostElRef}
						className="sc3-chrono-drag-ghost"
						role="presentation"
						aria-hidden="true"
					>
						{dragGhost.label}
					</div>,
					document.body
				)}
		</div>
	);
}

export default Scenario2Quiz2;
